<template>
    <v-container>
        <v-row>
            <v-col>
                <v-select
                    v-model="meetId"
                    :items="clubMeets"
                    label="Meet"
                    placeholder="Select a Meet"
                    no-data-text="No meets available to choose from"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table
                    :headers="headers"
                    :items="athletes"
                    :loading="mobilitiesLoading"
                    item-key="athlete_id"
                    disable-pagination
                    hide-default-footer
                    disable-sort
                    class="elevation-1"
                >
                    <template #[`item.avatar`]="{ item: athleteData }">
                        <athlete-avatar :person="athleteData.athlete" />
                    </template>

                    <template #[`item.first_name`]="{ item: athleteData }">
                        <inertia-link :href="`/athletes/${athleteData.athlete.id}`">
                            {{ athleteData.athlete.first_name }}
                        </inertia-link>
                    </template>

                    <template #[`item.last_name`]="{ item: athleteData }">
                        <inertia-link :href="`/athletes/${athleteData.athlete.id}`">
                            {{ athleteData.athlete.last_name }}
                        </inertia-link>
                    </template>

                    <template #[`item.tra_level`]="{ item: athleteData }">
                        {{ trampolineResult(athleteData)?.level }}
                    </template>

                    <template #[`item.tra_mobilized`]="{ item: athleteData }">
                        <mobility-check
                            v-if="trampolineResult(athleteData)"
                            :result="trampolineResult(athleteData)"
                            :mobility="trampolineResult(athleteData)?.mobility"
                            :meet="meet"
                        />
                    </template>

                    <template #[`item.dmt_level`]="{ item: athleteData }">
                        {{ doubleMiniResult(athleteData)?.level }}
                    </template>

                    <template #[`item.dmt_mobilized`]="{ item: athleteData }">
                        <mobility-check
                            v-if="doubleMiniResult(athleteData)"
                            :result="doubleMiniResult(athleteData)"
                            :mobility="doubleMiniResult(athleteData)?.mobility"
                            :meet="meet"
                        />
                    </template>

                    <template #[`item.tum_level`]="{ item: athleteData }">
                        {{ tumblingResult(athleteData)?.level }}
                    </template>

                    <template #[`item.tum_mobilized`]="{ item: athleteData }">
                        <mobility-check
                            v-if="tumblingResult(athleteData)"
                            :result="tumblingResult(athleteData)"
                            :mobility="tumblingResult(athleteData)?.mobility"
                            :meet="meet"
                        />
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import get from 'lodash/get.js';
import first from 'lodash/first.js';
import has from 'lodash/has.js';
import numeral from 'numeral';
import AthleteAvatar from '@/Components/ScorsyncAvatar.vue';
import Athlete from '@/Models/Athlete.js';
import Meet from '@/Models/Meet.js';
import User from '@/Models/User.js';
import MobilityCheck from '@/Components/MobilityCheck.vue';

export default {
    name: 'ClubMobilities',

    components: {
        AthleteAvatar,
        MobilityCheck,
    },

    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        meetId: null,
        athletes: [],
        mobilitiesLoading: false,
        headers: [
            {
                text: '',
                sortable: false,
                value: 'avatar',
            },
            {
                text: 'First Name',
                sortable: true,
                value: 'first_name',
            },
            {
                text: 'Last Name',
                sortable: true,
                value: 'last_name',
            },
            {
                text: 'TRA Level',
                sortable: true,
                value: 'tra_level',
            },
            {
                text: 'TRA M',
                sortable: true,
                value: 'tra_mobilized',
            },
            {
                text: 'DMT Level',
                sortable: true,
                value: 'dmt_level',
            },
            {
                text: 'DMT M',
                sortable: true,
                value: 'dmt_mobilized',
            },
            {
                text: 'TUM Level',
                sortable: true,
                value: 'tum_level',
            },
            {
                text: 'TUM M',
                sortable: true,
                value: 'tum_mobilized',
            },
        ],
    }),

    computed: {
        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().find(this.$page.props.auth.user.id);
        },

        clubMeets() {
            return Meet.query()
                .whereIdIn(_.map(this.$page.props.meets, 'id'))
                .get()
                .map(meet => ({
                    text: meet.title,
                    value: meet.id,
                }));
        },

        meet() {
            return Meet.find(this.meetId);
        },
    },

    watch: {
        meetId() {
            this.loadMobilities();
        },
    },

    created() {
        this.meetId = this.clubMeets[0]?.value;
    },

    mounted() {
        if (this.query && this.query.length > 0) {
            this.$refs.search.$el.getElementsByTagName('input')[0].focus();
        }
    },

    methods: {
        get,
        has,
        numeral,

        async loadMobilities() {
            this.mobilitiesLoading = true;

            const { data } = await axios.get(`/api/clubs/${this.clubId}/meets/${this.meetId}/mobilities`);

            this.athletes = data.data.map(athleteData => {
                athleteData.athlete = Athlete.find(athleteData.athlete.id);

                return athleteData;
            });

            this.mobilitiesLoading = false;
        },

        trampolineResult(athleteData) {
            return first(athleteData.results.filter(result => result.event === 'TI'));
        },

        doubleMiniResult(athleteData) {
            return first(athleteData.results.filter(result => result.event === 'DM'));
        },

        tumblingResult(athleteData) {
            return first(athleteData.results.filter(result => result.event === 'TU'));
        },

        changePage(page) {
            this.$inertia.visit(`${this.endpoint}?page=${page}`, { preserveScroll: true });
        },

        isQualifiedForEvent(qualifier, event) {
            const competed = get(qualifier, `qualifications.${event}.competed`);
            const level = get(qualifier, `qualifications.${event}.level`);
            return competed && !['1', '2', '3', '4'].includes(level);
        },
    },
};
</script>
